import Style from 'ol/style/Style';
import Stroke from 'ol/style/Stroke';
import {capitalize, isWinterLayer} from "../utils";


const LAND_TO_LAYER = {
  'wander': 'Wanderland',
  'velo': 'Veloland',
  'mountainbike': 'Mtbland',
  'skating': 'Skatingland',
  'kanu': 'Kanuland',
  'winterhiking': 'Winterhiking',
  'snowshoe': 'Snowshoe',
  'crosscountry': 'Crosscountry',
  'sledging': 'Sledging',
};

const LAND_OUTER_COLOR = {
  'velo': [0, 51, 255],
  'mountainbike': [102, 51, 0],
  'skating': [92, 52, 154],
  'kanu': [23, 90, 80],
  'winterhiking': [206, 5, 156],
  'snowshoe': [127, 3, 127],
  'crosscountry': [2, 116, 158],
  'sledging': [108, 47, 5],
};

const LAND_INNER_COLOR = {
  'velo': [0, 153, 255],
  'mountainbike': [204, 102, 0],
  'skating': [135, 91, 204],
  'kanu': [0, 135, 112],
  'winterhiking': [255, 132, 255],
  'snowshoe': [204, 51, 204],
  'crosscountry': [51, 204, 255],
  'sledging': [153, 92, 50],
};

const LAND_Z_INDEX = {
  'wander': 40,
  'velo': 30,
  'mountainbike': 20,
  'skating': 10,
  'kanu': 0,
  'winterhiking': 30,
  'snowshoe': 20,
  'crosscountry': 10,
  'sledging': 0,
};

const SURFACE_STROKE_OPTIONS = {
  1: {
    lineJoin: 'round',
  },
  2: {
    lineCap: 'butt',
    lineDash: [16, 8],
    lineJoin: 'round',
  },
  3: {
    width: 9,
    lineCap: 'round',
    lineDash: [0, 15],
    lineJoin: 'round',
  }
};

const styleCache = {};

/**
 * @param {string} land
 * @param {string} category
 * @param {Object<string, boolean>} selection
 */
function isActive(land, category, selection) {
  const landName = LAND_TO_LAYER[land];
  console.assert(landName);
  const categoryName = capitalize(category.replace('local', 'lokal'));
  // The first test is for the winter layers and the second one for the summer layers
  return selection[landName] === true || selection[`${landName}Etappen${categoryName}`] === true;
}

/**
 * @param {string} land
 * @param {string} category
 * @param {number} resolution
 */
function isVisible(land, category, resolution) {
  return isWinterLayer(land) || ((category !== 'local' && category !== 'handicap') || resolution <= 100);
}

/**
 * @param {string} land
 * @param {number} surface
 * @param {number} resolution
 * @return {Array<Style>}
 */
function routeStyle(land, surface, resolution) {
  // not dashed lines when the resolution is big
  surface = resolution > 20 ? 1 : surface;
  const innerWidth = resolution > 20 ? 4 : 6;

  const key = `${land}_${surface}_${innerWidth}`;

  let styles = styleCache[key];
  if (!styles) {
    const innerStrokeOptions = Object.assign({
      color: LAND_INNER_COLOR[land],
      width: innerWidth
    }, SURFACE_STROKE_OPTIONS[surface]);

    const outerStrokeOptions = Object.assign({
      color: LAND_OUTER_COLOR[land],
      width: innerWidth + 2
    }, SURFACE_STROKE_OPTIONS[surface]);

    const zIndex = LAND_Z_INDEX[land];
    styles = [
      new Style({
        zIndex: zIndex,
        stroke: new Stroke(outerStrokeOptions)
      }),
      new Style({
        zIndex: zIndex + 1,
        stroke: new Stroke(innerStrokeOptions)
      })
    ];
    styleCache[key] = styles;
  }

  return styles;
}

/**
 * @param {number} resolution
 * @return {Style}
 */
function wanderlandRouteStyle(resolution) {
  let width;
  if (resolution < 10) {
    width = 12;
  } else if (resolution < 50) {
    width = 10;
  } else {
    width = 6;
  }
  const key = `wander_${width}`;

  let style = styleCache[key];
  if (!style) {
    style = new Style({
      zIndex: LAND_Z_INDEX['wander'],
      stroke: new Stroke({
        color: [0, 168, 0],
        width: width
      })
    });
    styleCache[key] = style;
  }

  return style;
}

/**
 * @param {number} resolution
 * @return {Style}
 */
function crosscountryRouteStyle(resolution) {
  let width;
  if (resolution < 10) {
    width = 14;
  } else if (resolution < 50) {
    width = 12;
  } else {
    width = 7;
  }
  const key = `crosscountry_${width}`;

  let style = styleCache[key];
  if (!style) {
    style = new Style({
      zIndex: LAND_Z_INDEX['crosscountry'],
      stroke: new Stroke({
        color: [51, 204, 255],
        width: width,
      })
    });
    styleCache[key] = style;
  }

  return style;
}


/**
 * @param {Object<string, boolean>} selection
 */
export function createRouteStyle(selection) {
  if (Object.keys(selection).length === 0) {
    return () => undefined;
  }
  return (feature, resolution) => {
    const layer = feature.get('layer');
    const land = feature.get('land');
    if (layer === 'mvt_feeder_routes') {
      if (land === 'wander') {
        return [wanderlandRouteStyle(resolution)];
      } else if (land === 'crosscountry') {
        return [crosscountryRouteStyle(resolution)];
      } else {
        return routeStyle(land, 1, resolution);
      }
    }
    const category = feature.get('category');
    if (isActive(land, category, selection) && isVisible(land, category, resolution)) {
      if (land === 'wander') {
        return [wanderlandRouteStyle(resolution)];
      } else if (land === 'crosscountry') {
        return [crosscountryRouteStyle(resolution)];
      } else {
        return routeStyle(land, feature.get('surface'), resolution);
      }
    }
  };
}
